import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import './Dashboard.css';

export default function AdminDashboard() {
  const [tab, setTab] = useState('referrals');
  const [referrals, setReferrals] = useState([]);
  const [users, setUsers] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [paymentReference, setPaymentReference] = useState({});

  useEffect(() => {
    setLoading(true);
    setMessage('');
    if (tab === 'referrals') {
      apiClient.get('/referrals').then((res) => setReferrals(res.data.referrals)).finally(() => setLoading(false));
    } else if (tab === 'users') {
      apiClient.get('/users').then((res) => setUsers(res.data.users)).finally(() => setLoading(false));
    } else if (tab === 'appointments') {
      Promise.all([
        apiClient.get('/appointments'),
        apiClient.get('/users/clinicians'),
      ]).then(([aptRes, clinRes]) => {
        setAppointments(aptRes.data.appointments);
        setClinicians(clinRes.data.clinicians || []);
      }).finally(() => setLoading(false));
    }
  }, [tab]);

  async function updateReferralStatus(id, status) {
    const res = await apiClient.put('/referrals/' + id, { status });
    setReferrals((rs) => rs.map((r) => (r.id === id ? res.data.referral : r)));
  }

  async function assignClinician(apptId, clinicianId) {
    try {
      const res = await apiClient.put('/appointments/' + apptId + '/confirm', { clinicianId });
      setAppointments((as) => as.map((a) => (a.id === apptId ? res.data.appointment : a)));
      setMessage('Clinician assigned and appointment confirmed.');
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not assign clinician.');
    }
  }

  async function updatePayment(apptId, paymentStatus) {
    if (paymentStatus === 'paid') {
      const reference = (paymentReference[apptId] || '').trim();

      if (!reference) {
        setMessage('Enter the M-Pesa receipt/reference number before marking the appointment as paid.');
        return;
      }

      try {
        const res = await apiClient.put('/appointments/' + apptId + '/payment-status', {
          paymentStatus,
          providerReference: reference,
        });
        setAppointments((as) => as.map((a) => (a.id === apptId ? res.data.appointment : a)));
        setPaymentReference((refs) => ({ ...refs, [apptId]: '' }));
        setMessage('Payment recorded. Invoice and receipt will be generated automatically.');
      } catch (err) {
        setMessage(err.response?.data?.message || 'Could not update payment.');
      }

      return;
    }

    try {
      const res = await apiClient.put('/appointments/' + apptId + '/payment-status', { paymentStatus });
      setAppointments((as) => as.map((a) => (a.id === apptId ? res.data.appointment : a)));
      setMessage('Payment status updated.');
    } catch (err) {
      setMessage(err.response?.data?.message || 'Could not update payment.');
    }
  }

  return (
    <div className="dashboard-page">
      <div className="container">
        <div className="dashboard-header">
          <div>
            <span className="eyebrow">Admin</span>
            <h1>Platform overview</h1>
          </div>
        </div>

        {message && <div className="alert alert-success">{message}</div>}

        <div className="dashboard-tabs">
          <button className={'dashboard-tab ' + (tab === 'referrals' ? 'active' : '')} onClick={() => setTab('referrals')}>Referrals</button>
          <button className={'dashboard-tab ' + (tab === 'users' ? 'active' : '')} onClick={() => setTab('users')}>Users</button>
          <button className={'dashboard-tab ' + (tab === 'appointments' ? 'active' : '')} onClick={() => setTab('appointments')}>Appointments</button>
        </div>

        {loading ? <p>Loading...</p> : (
          <>
            {tab === 'referrals' && (
              <table className="data-table card">
                <thead>
                  <tr><th>Client</th><th>Organization</th><th>Urgency</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {referrals.map((r) => (
                    <tr key={r.id}>
                      <td>{r.patientFullName}</td>
                      <td>{r.partnerOrganizationName || '-'}</td>
                      <td>{r.urgency}</td>
                      <td><span className={'status-pill status-' + (r.status === 'received' ? 'requested' : 'confirmed')}>{r.status}</span></td>
                      <td>
                        <select value={r.status} onChange={(e) => updateReferralStatus(r.id, e.target.value)}>
                          <option value="received">Received</option>
                          <option value="contacted">Contacted</option>
                          <option value="booked">Booked</option>
                          <option value="declined">Declined</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                  {!referrals.length && <tr><td colSpan={5}>No referrals yet.</td></tr>}
                </tbody>
              </table>
            )}

            {tab === 'users' && (
              <table className="data-table card">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Role</th><th>Active</th></tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.fullName}</td>
                      <td>{u.email}</td>
                      <td>{u.role}</td>
                      <td>{u.isActive ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                  {!users.length && <tr><td colSpan={4}>No users found.</td></tr>}
                </tbody>
              </table>
            )}

            {tab === 'appointments' && (
              <div className="appt-list">
                {appointments.map((a) => (
                  <div className="card appt-card" key={a.id}>
                    <div>
                      <h4>{a.patient ? a.patient.fullName : 'Unknown Patient'}</h4>
                      <p className="appt-meta">
                        {new Date(a.scheduledAt).toLocaleString()} - {a.appointmentType}
                        {a.Service ? ' - ' + a.Service.name : ''}
                      </p>
                    </div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className={'status-pill status-' + a.status}>{a.status.replace('_', ' ')}</span>
                      <span className={'status-pill status-' + a.paymentStatus}>{a.paymentStatus}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                      <div>
                        <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Assign clinician:</label>
                        <select value={a.clinicianId || ''} onChange={(e) => { if (e.target.value) assignClinician(a.id, e.target.value); }} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border, #ddd)' }}>
                          <option value="">Unassigned</option>
                          {clinicians.map((c) => (
                            <option key={c.id} value={c.id}>{c.fullName} - {c.specialization || 'General'}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Payment:</label>
                        <select value={a.paymentStatus} onChange={(e) => updatePayment(a.id, e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border, #ddd)' }}>
                          <option value="unpaid">Unpaid</option>
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="refunded">Refunded</option>
                        </select>
                        {a.paymentStatus !== 'paid' && (
                          <input
                            type="text"
                            value={paymentReference[a.id] || ''}
                            onChange={(e) => setPaymentReference((refs) => ({ ...refs, [a.id]: e.target.value }))}
                            placeholder="M-Pesa receipt/reference"
                            aria-label="M-Pesa receipt/reference"
                            style={{ display: 'block', marginTop: 6, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border, #ddd)', minWidth: 220 }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {!appointments.length && <div className="card empty-state">No appointments yet.</div>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
